import "./OcipError.css";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Button, Table, Modal } from "react-bootstrap";
import { getIvDataIndex } from "../../utils/ivErrorDataUtils";

/**
 * INVT / DAS String Error 표시 규칙
 * - state   : 0x3806 Inverter working state
 * - Warn    : 0x3807 Main alarm code
 * - fault0  : 0x3809 Main fault code
 * - fault1  : 0x380A Sub-fault code
 *
 * fault 는 단일 코드가 아니라 main/sub 조합으로 해석해야 함.
 * 예) 31 + 01 => F31-01
 */

const DAS_RUN_STATE = {
  0: "초기화",
  1: "대기",
  2: "정상 운전",
  3: "고장",
  4: "설정 중",
  5: "독립 운전",
};

const DAS_FAULT_MAP = {
  "01-01": "PV 역극성 접속",
  "01-02": "PV 전압 과다",
  "01-03": "PV 패널 단락",
  "01-04": "PV1 단락 (XD7-10KW)",
  "01-05": "PV2 단락 (XD7-10KW)",
  "01-06": "PV1 역극성 접속 (XD7-10KW)",
  "01-07": "PV2 역극성 접속 (XD7-10KW)",

  "03-01": "BUS 전압 저하",
  "03-02": "BUS 전압 상승",
  "03-03": "BUS 전압 불평형",
  "03-04": "하드웨어 BUS 과전압",

  "05-01": "인버터 하드웨어 과전류",
  "05-02": "인버터 소프트웨어 과전류",
  "05-03": "BOOST 하드웨어 과전류",
  "05-04": "BOOST 소프트웨어 과전류",
  "05-05": "보조 전원 하드웨어 TZ 이상",
  "05-06": "BUS 하드웨어 TZ 과전압",
  "05-07": "LLC 측 하드웨어 TZ 이상",
  "05-08": "Buck-Boost 소프트웨어 과전류",

  "06-01": "인버터 온도 이상",
  "06-02": "BOOST 이상",
  "06-03": "방열판 이상",
  "06-04": "주변 환경 이상",
  "06-05": "Buck-Boost 온도 이상",
  "06-06": "NTC 단선",

  "07-01": "절연 감지 이상",
  "08-01": "드라이브 이상",

  "09-01": "메인 DSP 수신 ARM 통신 이상",
  "09-02": "ARM 수신 메인 DSP 통신 이상",
  "09-03": "슬레이브 DSP 수신 ARM 통신 이상",
  "09-04": "ARM 수신 서브 DSP 통신 이상",
  "09-05": "마스터-슬레이브 칩 통신 이상 - 마스터 칩",
  "09-06": "마스터-슬레이브 칩 통신 이상 - 슬레이브 칩",
  "09-07": "DSP 와 AFCI 통신 이상",

  "10-01": "누설 전류 과다",
  "10-02": "30mA 급변 이상",
  "10-03": "60mA 급변 이상",
  "10-04": "150mA 급변 이상",

  "11-01": "릴레이 단선",
  "11-02": "릴레이 단락",
  "11-03": "릴레이 자체 점검 이상",

  "12-01": "내부 팬 전체 이상",

  "14-01": "R상 DCI 이상",
  "14-02": "S상 DCI 이상",
  "14-03": "T상 DCI 이상",

  "15-01": "PID BUS 전압 과다",

  "19-01": "AC 전압 검출 불일치",
  "19-02": "BUS 전압 검출 불일치",
  "19-03": "절연 전압 검출 불일치",
  "19-04": "PV 전압 검출 불일치",
  "19-05": "GFCI 불일치",
  "19-06": "BUS 전압 샘플링 이상",
  "19-07": "PV 전류 샘플링 이상",

  "31-01": "계통 저전압 1단계",
  "31-02": "계통 과전압 1단계",
  "31-03": "계통 전압 없음",
  "31-04": "계통 저전압 2단계",
  "31-05": "계통 과전압 2단계",
  "31-06": "기동 시 계통 저전압",
  "31-07": "기동 시 계통 과전압",
  "31-08": "순간 정전 과전압",
  "31-09": "단독 운전 과전압",
  "31-10": "계통 전압 진동",
  "31-11": "계통 저전압 3단계",
  "31-12": "계통 전압 위상 동기 이상",
  "31-13": "계통 상순서 이상",

  "33-01": "계통 저주파수 1단계",
  "33-02": "계통 과주파수 1단계",
  "33-03": "계통 저주파수 2단계",
  "33-04": "계통 과주파수 2단계",
  "33-05": "기동 시 계통 저주파수",
  "33-06": "기동 시 계통 과주파수",
  "33-07": "주파수 자체 시험 실패",

  "36-01": "대형 인버터 모델 식별 실패",

  "37-01": "원격 정지 명령",
  "37-02": "점검 중",

  "38-01": "스트링 1 이상",
  "38-02": "스트링 2 이상",
  "38-03": "스트링 3 이상",
  "38-04": "스트링 4 이상",
  "38-05": "스트링 5 이상",
  "38-06": "스트링 6 이상",
  "38-07": "스트링 7 이상",
  "38-08": "스트링 8 이상",
  "38-09": "스트링 9 이상",
  "38-10": "스트링 10 이상",
  "38-11": "스트링 11 이상",
  "38-12": "스트링 12 이상",
  "38-13": "스트링 13 이상",
  "38-14": "스트링 14 이상",
  "38-15": "스트링 15 이상",
  "38-16": "스트링 16 이상",
  "38-17": "스트링 17 이상",
  "38-18": "스트링 18 이상",
  "38-19": "스트링 19 이상",
  "38-20": "스트링 20 이상",

  "39-01": "스트링 1 자가 점검 이상",
  "39-02": "스트링 2 자가 점검 이상",
  "39-03": "스트링 3 자가 점검 이상",
  "39-04": "스트링 4 자가 점검 이상",
  "39-05": "스트링 5 자가 점검 이상",
  "39-06": "스트링 6 자가 점검 이상",
  "39-07": "스트링 7 자가 점검 이상",
  "39-08": "스트링 8 자가 점검 이상",
  "39-09": "스트링 9 자가 점검 이상",
  "39-10": "스트링 10 자가 점검 이상",
  "39-11": "스트링 11 자가 점검 이상",
  "39-12": "스트링 12 자가 점검 이상",
  "39-13": "스트링 13 자가 점검 이상",
  "39-14": "스트링 14 자가 점검 이상",
  "39-15": "스트링 15 자가 점검 이상",
  "39-16": "스트링 16 자가 점검 이상",
  "39-17": "스트링 17 자가 점검 이상",
  "39-18": "스트링 18 자가 점검 이상",
  "39-19": "스트링 19 자가 점검 이상",
  "39-20": "스트링 20 자가 점검 이상",

  "40-01": "멀티 호스트 이상",
  "40-02": "병렬 CAN 통신 이상",
  "40-03": "호스트 손실 이상",
  "40-04": "동기 제로크로싱 이상",

  "41-01": "자동 시험 실패",
  "42-01": "N-PE 이상 전압",
  "43-01": "누설 전류 센서 이상",
  "44-01": "스트링 이상",
  "45-01": "보조 전원 정전",
  "46-01": "EPS 단락 이상",
};

function safePickPerInverter(value, dataIndex) {
  if (Array.isArray(value)) {
    if (typeof dataIndex === "number" && dataIndex >= 0) return value[dataIndex];
    return undefined;
  }
  return value;
}

function normalizeInt(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? Math.trunc(v) : null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function pad2(n) {
  if (n === null || n === undefined) return "N/A";
  const x = Number(n);
  if (!Number.isFinite(x)) return "N/A";
  return String(Math.trunc(x)).padStart(2, "0");
}

function makeCodeKey(mainCode, subCode) {
  if (mainCode === null || subCode === null) return null;
  return `${pad2(mainCode)}-${pad2(subCode)}`;
}

const DasTechStringError = ({ SelInverter }) => {
  const ivData = useSelector((state) => state.ivData || {});

  const [openModal, setOpenModal] = useState(null);
  const closeModal = () => setOpenModal(null);
  const dataIndex = getIvDataIndex(ivData, SelInverter);

  const runState = useMemo(() => {
    const raw =
      ivData?.state ??
      ivData?.mode ??
      ivData?.run_state ??
      ivData?.Status ??
      ivData?.status ??
      null;

    return normalizeInt(safePickPerInverter(raw, dataIndex));
  }, [ivData, dataIndex]);

  const mainAlarm = useMemo(() => {
    const raw =
      ivData?.Warn ??
      ivData?.warn ??
      ivData?.alarm ??
      ivData?.alarm_code ??
      ivData?.Pfault ??
      null;

    return normalizeInt(safePickPerInverter(raw, dataIndex));
  }, [ivData, dataIndex]);

  const mainFault = useMemo(() => {
    const raw =
      ivData?.fault0 ??
      ivData?.Fault0 ??
      ivData?.main_fault ??
      ivData?.Error?.[1] ??
      null;

    return normalizeInt(safePickPerInverter(raw, dataIndex));
  }, [ivData, dataIndex]);

  const subFault = useMemo(() => {
    const raw =
      ivData?.fault1 ??
      ivData?.Fault1 ??
      ivData?.sub_fault ??
      ivData?.Error?.[2] ??
      null;

    return normalizeInt(safePickPerInverter(raw, dataIndex));
  }, [ivData, dataIndex]);

  const faultKey = useMemo(() => {
    return makeCodeKey(mainFault, subFault);
  }, [mainFault, subFault]);

  const faultDisplayCode = useMemo(() => {
    if (!faultKey) return "N/A";
    return `F${faultKey}`;
  }, [faultKey]);

  const faultLabel = useMemo(() => {
    if (mainFault == null || subFault == null) return null;
    if (Number(mainFault) === 0 && Number(subFault) === 0) return "정상";
    return DAS_FAULT_MAP[faultKey] ?? `알 수 없는 고장 (${faultDisplayCode})`;
  }, [mainFault, subFault, faultKey, faultDisplayCode]);

  const runLabel = useMemo(() => {
    if (runState == null) return null;
    return DAS_RUN_STATE[runState] ?? `알 수 없음 (${runState})`;
  }, [runState]);

  const isFaultAbnormal = useMemo(() => {
    if (mainFault == null || subFault == null) return false;
    return !(Number(mainFault) === 0 && Number(subFault) === 0);
  }, [mainFault, subFault]);

  const isRunAbnormal = useMemo(() => {
    if (runState == null) return false;
    return Number(runState) !== 2;
  }, [runState]);

  const isAlarmAbnormal = useMemo(() => {
    if (mainAlarm == null) return false;
    return Number(mainAlarm) !== 0;
  }, [mainAlarm]);

  const faultRows = useMemo(() => {
    return Object.entries(DAS_FAULT_MAP)
      .sort(([a], [b]) => {
        const [a1, a2] = a.split("-").map(Number);
        const [b1, b2] = b.split("-").map(Number);
        if (a1 !== b1) return a1 - b1;
        return a2 - b2;
      })
      .map(([key, desc]) => ({
        key,
        code: `F${key}`,
        desc,
      }));
  }, []);

  const renderModalContent = () => {
    if (openModal === "FAULT") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>고장 코드</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <div>
                <b>메인 고장:</b> {mainFault == null ? "N/A" : pad2(mainFault)}
              </div>
              <div>
                <b>서브 고장:</b> {subFault == null ? "N/A" : pad2(subFault)}
              </div>
              <div>
                <b>표시 코드:</b> {faultDisplayCode}
              </div>
              <div>
                <b>의미:</b> {faultLabel ?? "N/A"}
              </div>
            </div>

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>표시 코드</th>
                  <th style={{ width: 90 }}>메인</th>
                  <th style={{ width: 90 }}>서브</th>
                  <th>의미</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  className={
                    mainFault === 0 && subFault === 0 ? "active-row" : ""
                  }
                >
                  <td>정상</td>
                  <td>00</td>
                  <td>00</td>
                  <td>고장 없음</td>
                </tr>

                {faultRows.map((row) => {
                  const [m, s] = row.key.split("-").map(Number);
                  const isActive = mainFault === m && subFault === s;

                  return (
                    <tr key={row.key} className={isActive ? "active-row" : ""}>
                      <td>{row.code}</td>
                      <td>{pad2(m)}</td>
                      <td>{pad2(s)}</td>
                      <td>{row.desc}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    if (openModal === "RUN") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>운전 상태</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <div>
                <b>현재 코드:</b> {runState == null ? "N/A" : runState}
              </div>
              <div>
                <b>의미:</b> {runLabel ?? "N/A"}
              </div>
            </div>

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>코드</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(DAS_RUN_STATE)
                  .map(Number)
                  .sort((a, b) => a - b)
                  .map((code) => {
                    const isActive = runState === code;
                    return (
                      <tr key={code} className={isActive ? "active-row" : ""}>
                        <td>{code}</td>
                        <td>{DAS_RUN_STATE[code]}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    if (openModal === "ALARM") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>알람 코드</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <div>
                <b>현재 코드:</b> {mainAlarm == null ? "N/A" : pad2(mainAlarm)}
              </div>
              <div style={{ fontSize: 13, opacity: 0.8, marginTop: 8 }}>
                * 현재 컴포넌트는 메인 알람 숫자값을 우선 표시합니다.
                <br />
                * 알람 상세 문구는 관련 문서가 확보되면 같은 방식으로 확장할 수 있습니다.
              </div>
            </div>

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>판정</th>
                  <th>의미</th>
                </tr>
              </thead>
              <tbody>
                <tr className={mainAlarm === 0 ? "active-row" : ""}>
                  <td>00</td>
                  <td>알람 없음</td>
                </tr>
                <tr
                  className={
                    mainAlarm != null && mainAlarm !== 0 ? "active-row" : ""
                  }
                >
                  <td>01 이상</td>
                  <td>메인 알람 발생</td>
                </tr>
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    return null;
  };

  return (
    <div className="status-list-container">
      <div className="status-list">
        <div className="status-item">
          <span className="status-value">
            고장 코드:{" "}
            {mainFault == null || subFault == null
              ? "N/A"
              : `${pad2(mainFault)} / ${pad2(subFault)} (${faultDisplayCode})`}
            {"  "}
            {faultLabel ? `- ${faultLabel}` : ""}
          </span>
          <button onClick={() => setOpenModal("FAULT")}>
            <span className={isFaultAbnormal ? "blinking-icon" : ""}>
              {isFaultAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>

        <div className="status-item">
          <span className="status-value">
            알람 코드: {mainAlarm == null ? "N/A" : pad2(mainAlarm)}
          </span>
          <button onClick={() => setOpenModal("ALARM")}>
            <span className={isAlarmAbnormal ? "blinking-icon" : ""}>
              {isAlarmAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>

        <div className="status-item">
          <span className="status-value">
            운전 상태: {runState == null ? "N/A" : `${runState} (${runLabel})`}
          </span>
          <button onClick={() => setOpenModal("RUN")}>
            <span className={isRunAbnormal ? "blinking-icon" : ""}>
              {isRunAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>
      </div>

      {openModal !== null && (
        <Modal show onHide={closeModal} size="lg">
          {renderModalContent()}
        </Modal>
      )}
    </div>
  );
};

export default DasTechStringError;
